import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';

import ProgressBar from '../../components/common/progressBar/progressBar';
import Header from '../../components/header/header';
import QuestionWrapper from '../../components/questionWrapper/questionWrapper';
import AssessmentSubHeader from '../../components/assessmentSubHeader/assessmentSubHeader';
import utilities from '../../utils/utils';
import './assessment.css';
import AssessmentSkeleton from '../../components/skeletons/assessmentSkeleton/assessmentSkeleton';
import connector from '../../connector/connector';
import BannerImage from '../../components/bannerImage/bannerImage';
import Modal from 'react-modal';
import Footer from '../../components/footer/footer';
import config from '../../config/config';

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
    },
};

const Assessment = () => {
    const [displayQuestions, setDisplayQuestions] = useState([]);
    const [progressValue, setProgressValue] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [cheerBannerContent, setCheerBannerContent] = useState(null);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [, forceUpdate] = useState(0);
    const { category } = useParams();
    const navigate = useNavigate();
    const [headerBannerContent, setHeaderBannerContent] = useState(null);



    const openModal = () => {
        setIsOpen(true);
    }
    const closeModal = () => {
        setIsOpen(false);
    }



    // Memoized filtered questions to avoid recalculation on every render
    const filteredQuestions = useMemo(() =>
        displayQuestions.filter(q =>
            q.category.toUpperCase() === category?.toUpperCase()
        ), [displayQuestions, category]
    );

    // Memoized next category calculation
    const nextCategory = useMemo(() =>
        utilities.returnNextCategory(category), [category]
    );

    // Extract API calls into separate functions
    const fetchAssessmentId = useCallback(async () => {
        if (localStorage.getItem('assessmentId')) return;

        // try {
        //     const response = await connector.getNewAssessmentId();
        //     console.log('Fetched assessment ID:', response);
        //     localStorage.setItem('assessmentId', response.data.assessmentId);
        // } catch (error) {
        //     console.error('Error fetching assessment ID:', error);
        // }
    }, []);

    const fetchQuestions = useCallback(async () => {
        try {
            const response = await connector.getQuestions(category);
            setDisplayQuestions(response);
            // console.log('Fetched questions:', response);
            utilities.questionCount = response.length;
        } catch (error) {
            console.error('Error fetching questions:', error);
        }
    }, [category]); 

    const displayCheerbanner = (content) => {
        setCheerBannerContent(content);
        setTimeout(() => setCheerBannerContent(null), 5000);
    }

    // Combine data fetching into a single function
    const initializeAssessment = useCallback(async () => {
        setIsLoading(true);
        await Promise.all([
            fetchAssessmentId(),
            fetchQuestions()
        ]);
        setProgressValue(utilities.getAnsweredQuestionsCount());
        setIsLoading(false);
    }, [fetchAssessmentId, fetchQuestions]);

    const setSurveyProgressMessage = (questionsAnswered) => {
        const milestones = [25, 50, 75];
        const progress = Math.floor((questionsAnswered / 32) * 100);

        // Get shown milestones from localStorage
        const shownMilestones = JSON.parse(localStorage.getItem('shownMilestones') || '[]');

        // Check for the next milestone to show
        for (const milestone of milestones) {
            if (progress >= milestone && !shownMilestones.includes(milestone)) {
                // Mark as shown
                shownMilestones.push(milestone);
                localStorage.setItem('shownMilestones', JSON.stringify(shownMilestones));

                // Return milestone message
                return `🎉 You've completed ${milestone}% of the survey!`;
            }
        }

        // No new message to show
        return null;
    }

    // Simplified useEffect - only run once on mount and when category changes
    useEffect(() => {
        if (!category) { navigate('/assessment/policy'); return; }
        initializeAssessment();

        displayCheerbanner(setSurveyProgressMessage(utilities.getAnsweredQuestionsCount(), displayQuestions.length));

        //  if (utilities.getAnsweredQuestionsCount() > (displayQuestions.length / 2)) {
        //             if (!localStorage.getItem('half')) {
        //                 localStorage.setItem('half', true)
        //                 displayCheerbanner('You are more than halfway done. Keep up the good work!')
        //             }
        //         }

        //         if (utilities.getAnsweredQuestionsCount() > (displayQuestions.length / 4)) {
        //             if (!localStorage.getItem('half') && !localStorage.getItem('threeQuarters')) {
        //                 localStorage.setItem('quarter', true)
        //                 displayCheerbanner('You are 25% of the way done! Keep up the good work!')
        //             }
        //         } else if(!localStorage.getItem('half') && !localStorage.getItem('threeQuarters')) {
        //             localStorage.setItem('threeQuarters', true)
        //             displayCheerbanner('You are 75% of the way done! Almost there!')
        //         }


    }, [category, navigate, initializeAssessment]);
    

    // Callback for progress updates
    const handleProgressUpdate = useCallback(() => {
        const answered = utilities.getAnsweredQuestionsCount();
        const total = displayQuestions.length;

        // Update progress bar
        setProgressValue(prev => {
            forceUpdate(x => x + 1);
            if (prev !== utilities.getAnsweredQuestionsCount()) return utilities.getAnsweredQuestionsCount();
            return prev;
        });

        // Possibly show cheer banner
        const message = setSurveyProgressMessage(answered, total);
        if (message) {
            displayCheerbanner(message);
        }
    }, [displayQuestions.length]);

    // Navigation handlers
    const navigateToSettings = useCallback(() => {
        navigate('/settings');
    }, [navigate]);

    const handleNextSection = useCallback(() => {

            if(!nextCategory && utilities.getAnsweredQuestionsCount() < 10){
                setHeaderBannerContent(`You have answered ${utilities.getAnsweredQuestionsCount()} ${utilities.getAnsweredQuestionsCount() === 1 ? 'question' : 'questions'}. Please answer at least 10 questions to see your results.`);
                setTimeout(() => setHeaderBannerContent(null), 5000);
                return;
            }

        const destination = nextCategory
            ? `/assessment/${nextCategory}`
            : '/assessment-results';
        navigate(destination);
    }, [navigate, nextCategory]);

    // Keyboard shortcut
    useHotkeys('ctrl+shift+s', navigateToSettings);



    return (
        <>
            <Header bannerContent={headerBannerContent || cheerBannerContent}/>
            
           
            <div className='assessment-wrapper'>
                <BannerImage text={`${config.applicationName} | ${utilities.capitalizeFirstLetter(category)}`}></BannerImage>
                {category === 'policy' && <div className='welcome-blurb'>
                    {/* <h2>Welcome to the AI Journey!</h2> */}
                    {/* <h5>Is your city ready for AI?</h5> */}
                    {/* <p>Cities manage services across an extraordinarily wide range of domains—everything from human services, drinking water supply and waste water, and building codes to education, law enforcement, and transportation. In the midst of all that, it can be hard for cities to know if they’re ready for AI—and how it can have a positive impact in their city.Knowing where and how to use a general-purpose Swiss Army knife tool like generative AI among the myriad different benefits and services the city provides can seem an ill-defined and intractable question—the kind of question that makes you stop before you even get started. To avoid that pitfall, GovEx designed a City AI Readiness Assessment that cities can take right now.</p> */}
                    <p>Welcome to the {config.applicationName}! This assessment contains 33 questions across 4 categories and will take about 10 minutes to complete. Though any local government official is welcome to complete the assessment, you may find staff in Technology, Innovation, Privacy, Performance Management, or Policy best-equipped to respond.</p>
                </div>}
                <ProgressBar key={progressValue} value={progressValue} total={displayQuestions.length} />
                <AssessmentSubHeader />
                <form className='assessment-form'>
                    {
                        isLoading ? <AssessmentSkeleton></AssessmentSkeleton> :
                            filteredQuestions.map((question, index) => (
                                <QuestionWrapper
                                    key={question.id || index}
                                    question={question}
                                    updateProgressFunc={handleProgressUpdate}
                                />
                            ))
                    }
                    <div className='buttons-wrapper'>

                        <button
                            className='btn-submit'
                            type='button'
                            onClick={openModal}
                        >
                            Save & Exit
                        </button><button
                            type="button"
                            className='fill-button'
                            onClick={handleNextSection}
                        >
                            {nextCategory ? 'Next Section' : 'Finalize & See Results'}
                        </button><br></br>
                        {/* {category === 'opportunities' && <a href='https://form.asana.com/?k=4XWA1HSIKdmMfB9GZZVNmA&d=1108016200678557' target='_blank' rel="noreferrer" className='feedback-link'>Submit Feedback</a>} */}
                    </div>
                </form>
            </div>

            <Modal
                isOpen={modalIsOpen}
                // onAfterOpen={afterOpenModal}
                onRequestClose={closeModal}
                style={customStyles}
                contentLabel="Example Modal"
            >
                <div className='modal-content-wrapper'>
                    <h2>Save & Exit</h2>
                    {/* <button onClick={closeModal}>close</button> */}
                    <div className='modal-text-wrapper'>
                        <p>Your progress is automatically saved as you answer each question. You can exit the assessment at any time and return later to complete it.</p>
                        <p>Please note that responses are saved locally on your machine, so come back to this computer when you are ready to pick up where you left off.</p>
                        <button onClick={closeModal}>Close</button>
                    </div>
                </div>
            </Modal>
            <Footer showFeedbackLink={category === 'opportunities'}></Footer>
            {category === 'opportunities' && <p className='feedback-tab'><a href='https://form.asana.com/?k=4XWA1HSIKdmMfB9GZZVNmA&d=1108016200678557' target='_blank' rel="noreferrer">Submit Feedback</a></p>}
        </>
    );
};

export default Assessment;